import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.barnApiEnv;
const baseUrl = API.address;

export type UploadComponentConfig = {
    spaceName: string;
    allowedTypes: string[];
    maxFileSizeMb: number;
    multipleFiles: boolean;
    maxFiles: number;
    uploadEndpoint: string;
    deduplicate: boolean;
    onDuplicate: 'reuse' | 'reject' | null;
};

export type UploadUrlRequest = {
    spaceName: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    hashSha256?: string;
    metadata?: Record<string, unknown>;
    // Upload TIPADO (opcionais, retrocompatíveis — AF-4107). Sem `documentType`, segue o
    // caminho legado (upload sem tipo). `referenceDate`/`documentDate` (YYYY-MM-DD) só podem
    // ser enviados JUNTO com `documentType` — o backend devolve 422 se forem enviados sem o tipo.
    documentType?: string;
    referenceDate?: string;
    documentDate?: string;
};

export type UploadUrlResponse = {
    fileId: string;
    uploadUrl: string;
    expiresIn: number;
    filePath: string;
    reused?: boolean;
};

export const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

export const getUploadComponent = async (spaceName: string): Promise<UploadComponentConfig> => {
    const response = await fetch(`${baseUrl}/spaces/${spaceName}/page-component`, {
        headers: await getHeaders(),
    });

    if (!response.ok) {
        throw new Error(`Failed to get upload component config: ${response.status}`);
    }

    return response.json();
};

export class BarnApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

export const requestUploadUrl = async (request: UploadUrlRequest): Promise<UploadUrlResponse> => {
    const response = await fetch(`${baseUrl}/files/upload-url`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new BarnApiError(response.status, error?.errors?.[0]?.message || `Upload URL request failed: ${response.status}`);
    }

    return response.json();
};

export const uploadFileToS3 = async (
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void,
    timeoutMs: number = 300000,
): Promise<void> => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        const timeoutId = setTimeout(() => {
            xhr.abort();
            reject(new Error('Upload timeout — arquivo muito grande ou conexão lenta'));
        }, timeoutMs);

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress(percent);
            }
        });

        xhr.addEventListener('load', () => {
            clearTimeout(timeoutId);
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`S3 upload failed: ${xhr.status}`));
            }
        });

        xhr.addEventListener('error', () => { clearTimeout(timeoutId); reject(new Error('Network error during upload')); });
        xhr.addEventListener('abort', () => { clearTimeout(timeoutId); reject(new Error('Upload aborted')); });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
    });
};

export type DownloadUrlResponse = {
    fileId: string;
    fileName: string;
    contentType: string;
    downloadUrl: string;
    expiresIn: number;
};

// URL assinada de leitura de um arquivo do barn (AF-3691). `inline` serve para pré-visualizar
// no browser; `attachment` força o download com o nome original. A URL é temporária (15 min).
export const getDownloadUrl = async (
    fileId: string,
    disposition: 'inline' | 'attachment' = 'inline',
): Promise<DownloadUrlResponse> => {
    const response = await fetch(
        `${baseUrl}/files/${fileId}/download-url?disposition=${disposition}`,
        { headers: await getHeaders() },
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new BarnApiError(
            response.status,
            error?.errors?.[0]?.message || `Download URL request failed: ${response.status}`,
        );
    }

    return response.json();
};

// Status de ingestão de um arquivo no Knowledge Base (esteira Docling → KB) — AF-4107.
// GET /files/{id}/ingestion devolve envelope {data, meta}. `status` ∈ NOT_STARTED/PENDING/
// PROCESSING/SYNCED/FAILED (NOT_STARTED é virtual: devolvido quando a esteira ainda não rodou).
export type IngestionStatusResponse = {
    fileId: string;
    status: string;
    statusFormatted: string;
    markdownUri?: string | null;
    kbDocumentRef?: string | null;
    kbSyncStatus?: string | null;
    error?: string | null;
    startedAt?: string | null;
    finishedAt?: string | null;
};

export const getIngestionStatus = async (fileId: string): Promise<IngestionStatusResponse> => {
    const response = await fetch(`${baseUrl}/files/${fileId}/ingestion`, {
        headers: await getHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new BarnApiError(
            response.status,
            error?.errors?.[0]?.message || `Ingestion status request failed: ${response.status}`,
        );
    }

    const result = await response.json();
    // Envelope da casa: { data: {...}, meta: {...} }.
    return result?.data ?? result;
};

// Tipos de documento do Barn (AF-4107). Dirigem os metadados: cada tipo expõe um
// `metadataSchema` (JSON Schema-like: properties + required?) com os campos que o upload
// tipado daquele tipo aceita. GET /document-types exige apenas JWT (não é ADMIN-only;
// só POST/DELETE exigem ADMIN). Resposta no envelope da casa {data, meta}.
export type DocumentTypeMetadataSchema = {
    type?: string;
    properties?: Record<string, unknown>;
    required?: string[];
    [key: string]: unknown;
};

export type DocumentType = {
    code: string;
    label: string;
    metadataSchema?: DocumentTypeMetadataSchema;
    ingestable: boolean;
};

export const listDocumentTypes = async (): Promise<DocumentType[]> => {
    const response = await fetch(`${baseUrl}/document-types`, {
        headers: await getHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new BarnApiError(
            response.status,
            error?.errors?.[0]?.message || `List document types failed: ${response.status}`,
        );
    }

    const result = await response.json();
    // Envelope da casa: { data: [...], meta: {...} }.
    return result?.data ?? [];
};

export const deleteFile = async (fileId: string): Promise<void> => {
    const response = await fetch(`${baseUrl}/files/${fileId}`, {
        method: 'DELETE',
        headers: await getHeaders(),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.errors?.[0]?.message || `Delete failed: ${response.status}`);
    }
};

export const computeSha256 = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};
